import React from 'react';

import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { Uuid } from '~/types/Uuid';

/**
 * Bolds any part of the message that is equal to the userName
 */
const parseMessageContent = (
  message: string,
  userName: string | null,
): React.ReactNode => {
  if (!userName || !message.includes(userName)) {
    return message;
  }

  const parts = message.split(userName);
  const result: React.ReactNode[] = [];

  parts.forEach((part) => {
    if (part === '') {
      result.push(<strong key={userName}>{userName}</strong>);
    } else {
      result.push(part);
    }
  });

  return result;
};

/**
 * Calls the getEsignatureActivities API and maps the response to a format that can be used by ActivityLog
 */
export const useEsignatureActivityLogs = ({ ticketId }: { ticketId: Uuid }) => {
  const isEsignatureRevampEnabled = useFlag(FlagType.EsignatureV2Revamp);
  const {
    data: esignatureActivities,
    isLoading,
    error,
  } = api.endpoints.getEsignatureActivities.useQuery(
    { ticketId },
    {
      skip: !isEsignatureRevampEnabled || !ticketId,
    },
  );

  const items =
    esignatureActivities?.activities.map((activity) => ({
      id: activity.date.toISOString(), // no id on esign activity, so create unique string with date for React key
      content: parseMessageContent(activity.message, activity.userName),
      contentSearchText: activity.message,
      dateTime: activity.date,
      quoteSearchText: undefined,
      quote: undefined,
    })) ?? [];

  return {
    items,
    isLoading,
    error,
  };
};
